import { type ReactNode, useId, useState } from "react";

/**
 * Lightweight hover/focus tooltip. Renders a styled popover (not the browser's
 * native `title`, which is unstyled, slow, and breaks multi-line layout).
 * Pure CSS visibility on hover + keyboard focus for accessibility.
 *
 * Usage:
 *   <Tooltip content={<>Multi-line\nguidance…</>}>
 *     <span className="…">?</span>
 *   </Tooltip>
 */
export function Tooltip({
  content,
  children,
  className = "",
}: {
  content: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span aria-describedby={open ? id : undefined} tabIndex={0} className="outline-none">
        {children}
      </span>
      {open && (
        <span
          id={id}
          role="tooltip"
          className="absolute left-1/2 top-full z-50 mt-1 w-64 -translate-x-1/2 whitespace-pre-line rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-left text-[11px] font-normal leading-relaxed text-text-secondary shadow-lg"
        >
          {content}
        </span>
      )}
    </span>
  );
}
